/**
 * Global Keyboard Shortcuts Manager
 * - Alt + S:
 *    1. If "Save and Next" / "Save" button is present -> Trigger Save and Next
 *    2. Otherwise on listing / table pages -> Focus Search Input
 * - Alt + Shift + S / Alt + /: Focus Search Input directly
 * - Alt + F: Toggle Filters Drawer / Panel
 * - Escape: Close Filters Drawer (when open)
 */

export const isElementVisible = (el) => {
  if (!el) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }
  return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
};

export const findSaveAndNextButton = () => {
  // 1. Check for elements with specific save-next classes or attributes
  const specificSelectors = [
    '.save-next-button',
    '[data-shortcut="save-next"]',
    '.save-next',
    'button.save-next'
  ];

  for (const selector of specificSelectors) {
    const el = document.querySelector(selector);
    if (el && isElementVisible(el) && !el.disabled) {
      return el;
    }
  }

  // 2. Query all visible buttons / clickable elements for "Save and Next" or "Save & Next"
  const allButtons = Array.from(
    document.querySelectorAll('button, [role="button"], input[type="button"], input[type="submit"], a.btn')
  );

  for (const btn of allButtons) {
    if (!isElementVisible(btn) || btn.disabled) continue;
    const text = (btn.innerText || btn.textContent || btn.value || '').trim().toLowerCase();
    if (
      text.includes('save and next') ||
      text.includes('save & next') ||
      text === 'save and next' ||
      text === 'save & next'
    ) {
      return btn;
    }
  }

  // 3. Check for visible modal/form "Save" buttons
  for (const btn of allButtons) {
    if (!isElementVisible(btn) || btn.disabled) continue;
    const text = (btn.innerText || btn.textContent || btn.value || '').trim().toLowerCase();
    if (
      text === 'save' ||
      text === 'save changes' ||
      text === 'save & continue' ||
      text === 'save and continue'
    ) {
      return btn;
    }
  }

  return null;
};

export const focusSearchInput = () => {
  const selectors = [
    '[data-shortcut="search"]',
    '.table-header-search-group input',
    'input[type="search"]',
    'input[placeholder*="search" i]',
    'input[name*="search" i]',
    '.search-input'
  ];

  for (const selector of selectors) {
    const elements = Array.from(document.querySelectorAll(selector));
    const target = elements.find(isElementVisible);
    if (target) {
      target.focus();
      if (typeof target.select === 'function') {
        target.select();
      }
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      return true;
    }
  }
  return false;
};

export const toggleFilterPanel = () => {
  // 1. If filter drawer is currently open, close it
  const openDrawer = document.querySelector('[data-filter-drawer="true"][data-open="true"]');
  if (openDrawer && isElementVisible(openDrawer)) {
    const closeBtn = openDrawer.querySelector(
      '[data-shortcut="filter-close"], button[aria-label*="close" i], svg[data-icon="close"]'
    );
    if (closeBtn) {
      closeBtn.click();
      return true;
    }
  }

  // 2. Query filter toggle button / trigger
  const selectors = [
    '[data-shortcut="filter"]',
    'button[aria-label*="filter" i]',
    'span[aria-label*="filter" i]',
    '[title*="filter" i]',
    '.filter-button',
    '.filter-icon'
  ];

  for (const selector of selectors) {
    const elements = Array.from(document.querySelectorAll(selector));
    const target = elements.find(isElementVisible);
    if (target) {
      target.click();
      return true;
    }
  }

  // 3. Fallback: Search for SVG icon of filter (FilterList)
  const filterSvgs = Array.from(document.querySelectorAll('svg')).filter((svg) => {
    const title = svg.getAttribute('aria-label') || svg.getAttribute('title') || '';
    const classList = svg.getAttribute('class') || '';
    return classList.includes('FilterList') || title.toLowerCase().includes('filter');
  });

  for (const svg of filterSvgs) {
    const clickable = svg.closest('button, [role="button"], span, div');
    if (clickable && isElementVisible(clickable)) {
      clickable.click();
      return true;
    }
  }

  return false;
};

export const setupKeyboardShortcuts = () => {
  const handleKeyDown = (e) => {
    // If the event was already consumed and prevented by a local component, do not re-process
    if (e.defaultPrevented) return;

    // Direct search shortcut: Alt + Shift + S or Alt + / (works unconditionally on any screen)
    if (
      (e.altKey && e.shiftKey && (e.key === 'S' || e.key === 's' || e.code === 'KeyS')) ||
      (e.altKey && !e.ctrlKey && (e.key === '/' || e.code === 'Slash'))
    ) {
      e.preventDefault();
      focusSearchInput();
      return;
    }

    // Standard Alt shortcuts (Alt + S and Alt + F)
    if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      const key = (e.key || '').toLowerCase();
      const code = e.code || '';

      if (key === 's' || code === 'KeyS') {
        // Priority 1: Check if a visible "Save and Next" / "Save" button is present on the page
        const saveNextBtn = findSaveAndNextButton();
        if (saveNextBtn) {
          e.preventDefault();
          saveNextBtn.click();
          return;
        }

        // Priority 2: If on a survey / bill / digitization screen, let local component's save shortcut execute
        const pathname = window.location.pathname.toLowerCase();
        const isSaveScreen =
          pathname.includes('/bills') ||
          pathname.includes('/collected-bills') ||
          pathname.includes('/survey') ||
          pathname.includes('/digitization');

        if (isSaveScreen) {
          // Do not hijack Alt + S on data-entry/save screens
          return;
        }

        // Priority 3: On listing / table / dashboard pages, focus Search
        e.preventDefault();
        focusSearchInput();
      } else if (key === 'f' || code === 'KeyF') {
        e.preventDefault();
        toggleFilterPanel();
      }
    }
  };

  window.addEventListener('keydown', handleKeyDown, false);

  return () => {
    window.removeEventListener('keydown', handleKeyDown, false);
  };
};

export default setupKeyboardShortcuts;
